// Personnel add/edit page. Reached via /personnel/add (no id) or
// /personnel/:id/edit (loads existing row first). Save / Cancel / Delete
// live in the sticky bottom bar.

import React, {useEffect, useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import {ChipSelector} from '../../components/common/ChipSelector'
import {FormField} from '../../components/common/FormField'
import {Header} from '../../components/Header'
import {PageShell} from '../../components/PageShell'
import {showToast} from '../../components/common/Toast'
import {supabase, DEFAULT_MINE_ID} from '../../app/supabaseClient'
import {
  CREWS,
  DESIGNATIONS,
  PERSONNEL_STATUSES,
  generateId,
  suggestEmployeeId,
} from '../../lib/constants'
import type {PersonnelDetail, PersonnelStatus} from './types'

interface FormErrors {
  name?: string
  employee_id?: string
  designation?: string
  crew?: string
}

export const PersonnelFormScreen: React.FC = () => {
  const {id} = useParams<{id?: string}>()
  const navigate = useNavigate()
  const isEdit = Boolean(id)

  const [loading, setLoading] = useState(isEdit)
  const [busy, setBusy] = useState(false)
  const [errors, setErrors] = useState<FormErrors>({})

  const [name, setName] = useState('')
  const [employeeId, setEmployeeId] = useState('')
  const [designation, setDesignation] = useState<typeof DESIGNATIONS[number] | null>(null)
  const [crew, setCrew] = useState<typeof CREWS[number] | null>(null)
  const [status, setStatus] = useState<PersonnelStatus>('tagged_out')

  // Load row in edit mode.
  useEffect(() => {
    if (!isEdit || !id) return
    let cancelled = false
    ;(async () => {
      setLoading(true)
      const {data, error} = await supabase
        .from('personnel')
        .select('id, name, employee_id, designation, crew, status, shift_id, location, photo')
        .eq('id', id)
        .single<PersonnelDetail>()
      if (cancelled) return
      if (error || !data) {
        showToast(`Load failed: ${error?.message ?? 'Not found'}`, 'error')
        navigate('/personnel')
        return
      }
      setName(data.name)
      setEmployeeId(data.employee_id)
      setDesignation((data.designation as any) ?? null)
      setCrew((data.crew as any) ?? null)
      setStatus(data.status)
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [id, isEdit, navigate])

  // Default chip values on add.
  useEffect(() => {
    if (isEdit) return
    setDesignation(DESIGNATIONS[0])
    setCrew(CREWS[0])
  }, [isEdit])

  const validate = (): boolean => {
    const e: FormErrors = {}
    if (!name.trim()) e.name = 'Name is required'
    if (isEdit && !employeeId.trim()) e.employee_id = 'Employee ID is required'
    if (!designation) e.designation = 'Pick a designation'
    if (!crew) e.crew = 'Pick a crew'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSave = async () => {
    if (!validate()) return
    setBusy(true)
    const now = new Date().toISOString()
    if (!isEdit) {
      const newId = generateId()
      const empId = employeeId.trim() || suggestEmployeeId()
      const {error} = await supabase.from('personnel').insert({
        id: newId,
        mine_id: DEFAULT_MINE_ID,
        name: name.trim(),
        employee_id: empId,
        designation,
        crew,
        status,
        photo: null,
        location: null,
        created_at: now,
        updated_at: now,
      })
      if (error) {
        showToast(`Add failed: ${error.message}`, 'error')
        setBusy(false)
        return
      }
      showToast('Personnel added.', 'success')
    } else {
      const {error} = await supabase
        .from('personnel')
        .update({
          name: name.trim(),
          employee_id: employeeId.trim(),
          designation,
          crew,
          status,
          updated_at: now,
        })
        .eq('id', id!)
      if (error) {
        showToast(`Update failed: ${error.message}`, 'error')
        setBusy(false)
        return
      }
      showToast('Personnel updated.', 'success')
    }
    setBusy(false)
    navigate('/personnel')
  }

  const handleDelete = async () => {
    if (!id) return
    if (!window.confirm(`Delete ${name} (${employeeId})?`)) return
    setBusy(true)
    // Unassign tags first.
    await supabase
      .from('tags')
      .update({assigned_to_id: null, assigned_to_type: null})
      .eq('assigned_to_id', id)
      .eq('assigned_to_type', 'personnel')
    const {error} = await supabase.from('personnel').delete().eq('id', id)
    setBusy(false)
    if (error) {
      showToast(`Delete failed: ${error.message}`, 'error')
      return
    }
    showToast('Personnel deleted.', 'success')
    navigate('/personnel')
  }

  const title = isEdit ? `Edit ${name || 'Personnel'}` : 'Add Personnel'
  const saveLabel = isEdit ? 'Save Changes' : 'Add Personnel'

  return (
    <main className="flex flex-1 flex-col overflow-hidden">
      <Header title={title} backTo="/personnel" />
      <PageShell
        footer={
          <>
            {isEdit && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={busy}
                className="mr-auto rounded-md border border-statusRed px-lg py-sm text-md text-statusRed hover:bg-statusRed hover:text-white disabled:opacity-40"
              >
                Delete
              </button>
            )}
            <button
              type="button"
              onClick={() => navigate('/personnel')}
              disabled={busy}
              className="rounded-md border border-surfaceBorder px-lg py-sm text-md text-textSecondary hover:border-textPrimary hover:text-textPrimary disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={busy}
              className="rounded-md bg-primary px-lg py-sm text-md font-bold text-black hover:bg-primaryLight disabled:opacity-40"
            >
              {busy ? 'Saving…' : saveLabel}
            </button>
          </>
        }
      >
        {loading ? (
          <div className="flex h-full items-center justify-center text-textMuted">Loading…</div>
        ) : (
          <div className="flex flex-col gap-lg">
            <div className="flex gap-lg">
              <FormField
                label="Name"
                value={name}
                onChange={setName}
                placeholder="Enter full name"
                error={errors.name ?? null}
              />
              <FormField
                label="Employee ID"
                value={employeeId}
                onChange={setEmployeeId}
                placeholder="EMP_xxxx (auto-generated if empty)"
                error={errors.employee_id ?? null}
              />
            </div>

            <ChipSelector
              label="Designation"
              options={DESIGNATIONS}
              value={designation}
              onChange={setDesignation}
              error={errors.designation ?? null}
            />

            <ChipSelector
              label="Crew"
              options={CREWS}
              value={crew}
              onChange={setCrew}
              error={errors.crew ?? null}
            />

            {isEdit && (
              <ChipSelector
                label="Status"
                options={PERSONNEL_STATUSES}
                value={status}
                onChange={setStatus}
              />
            )}
          </div>
        )}
      </PageShell>
    </main>
  )
}
